import { useMemo, useState } from 'react';
import { Box, DataTable, Link, Search, Stack, type DataTableFeatures } from '@customerio/pluma-components/react';
import type { ColumnDef } from '@tanstack/table-core';
import { useRouter } from '@tanstack/react-router';
import { objectTypeIcon, type ObjectType } from '@/services/db/object-type/entity';
import type { ObjectRecord } from '@/services/db/object/entity';
import { isEmptyString } from '@/utils/string';
import { ObjectFilterBuilder } from '../object-filter-builder';
import { SearchByControl } from './search-by-control';
import type { ObjectPicker } from './use-object-picker';

type ObjectNameSearchProps = {
  /** The committed term — seeds the input. */
  name: string;
  singularName: string;
  onSearch: (name: string) => void;
};

function ObjectNameSearch({ name, singularName, onSearch }: ObjectNameSearchProps) {
  const [value, setValue] = useState(name);
  // Re-seed when the committed term changes from outside (a mode switch clears it).
  const [committed, setCommitted] = useState(name);
  if (name !== committed) {
    setCommitted(name);
    setValue(name);
  }

  return (
    <Search
      className="fs-mask"
      ariaLabel={`${singularName} name search`}
      placeholder={`Find by ${singularName} name`}
      value={value}
      onSearchInput={setValue}
      onSearch={onSearch}
      shouldSearchOnEnter
      shouldSearchOnClear
    />
  );
}

type NameCellProps = { record: ObjectRecord; objectType: ObjectType; workspaceId: number };

function NameCell({ record, objectType, workspaceId }: NameCellProps) {
  const router = useRouter();
  const name = record.attributes.name ?? '';
  const href = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/$objectId',
    params: { workspaceId, slug: objectType.slug, objectId: record.id },
  }).href;
  return (
    <Link className="fs-mask" variant="primary" href={href}>
      {isEmptyString(name) ? '(No name)' : name}
    </Link>
  );
}

const getRowId = (record: ObjectRecord) => record.id;

function objectEmptyState(picker: ObjectPicker, objectType: ObjectType) {
  const icon = objectTypeIcon(objectType);
  if (picker.hasFilters) {
    return {
      title: isEmptyString(picker.params.search)
        ? 'No objects matching your filters'
        : 'No objects matching your search',
      icon,
    };
  }

  return {
    title: `Build your ${objectType.name}`,
    description: `Add ${objectType.name} to create relationships and more.`,
    icon,
  };
}

type ObjectSearchSlotProps = { picker: ObjectPicker; objectType: ObjectType };

function ObjectSearchSlot({ picker, objectType }: ObjectSearchSlotProps) {
  return (
    <>
      <SearchByControl
        searchLabel="name"
        conditionsLabel="attribute"
        value={picker.searchBy}
        onChange={picker.activateSearchBy}
      />
      <Box>
        {picker.searchBy === 'search' ? (
          <ObjectNameSearch
            name={picker.params.search}
            singularName={objectType.singular_name}
            onSearch={picker.search}
          />
        ) : (
          <ObjectFilterBuilder
            filters={picker.params.filters}
            objectTypeId={objectType.id}
            attributeNames={objectType.sent_attributes ?? []}
            onChange={picker.applyFilters}
          />
        )}
      </Box>
    </>
  );
}

type ObjectStepProps = {
  picker: ObjectPicker;
  objectType: ObjectType;
  workspaceId: number;
  isLoading: boolean;
  canSelect: boolean;
};

export function ObjectStep({ picker, objectType, workspaceId, isLoading, canSelect }: ObjectStepProps) {
  const columns = useMemo<ColumnDef<DataTableFeatures, ObjectRecord>[]>(
    () => [
      {
        id: 'name',
        header: 'name',
        cell: ({ row }) => <NameCell record={row.original} objectType={objectType} workspaceId={workspaceId} />,
      },
    ],
    [objectType, workspaceId],
  );
  const rowSelection = useMemo(
    () => Object.fromEntries(picker.selected.map((record) => [getRowId(record), true])),
    [picker.selected],
  );
  return (
    <Box data-test-add-relationships-modal-object-list>
      <Stack gap="150" mt="200">
        <ObjectSearchSlot picker={picker} objectType={objectType} />
        <DataTable
          caption={`Select ${objectType.name}`}
          data={[...picker.objects]}
          columns={columns}
          getRowId={getRowId}
          withPagination
          pagination={{
            page: picker.params.page,
            pageSize: picker.pageSize,
            totalItemCount: picker.total,
            onPageChange: picker.setPage,
            isManual: true,
          }}
          withRowSelection={canSelect}
          rowSelection={rowSelection}
          onRowSelectionChange={(selection) =>
            picker.setSelected(picker.objects.filter((record) => selection[getRowId(record)] === true))
          }
          emptyState={objectEmptyState(picker, objectType)}
          isLoading={isLoading}
        />
      </Stack>
    </Box>
  );
}
